import React, { useState, useEffect, useRef } from 'react';

const OnboardingChatBubble = ({
  message,
  avatar = '🎯',
  typing = false,
  delay = 0,
  onComplete = () => {},
  showTail = true,
  animated = true
}) => {
  const [isTyping, setIsTyping] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showLetters, setShowLetters] = useState(false);

  const messageRef = useRef(null);
  const completionTimerRef = useRef(null);

  // Split message into characters for animation
  const messageChars = message ? message.split('') : [];

  // Show bubble and start letter animations
  useEffect(() => {
    if (!message || !animated) {
      setIsVisible(true);
      setShowLetters(true);
      onComplete();
      return;
    }

    // Show bubble after delay
    const showTimer = setTimeout(() => {
      setIsVisible(true);
      setIsTyping(true);

      // Start showing letters immediately
      setShowLetters(true);

      // Calculate total animation time
      const letterDelay = 30; // milliseconds between each letter starting
      const animationDuration = 300; // duration of each letter's animation
      const totalTime = (messageChars.length * letterDelay) + animationDuration;

      // Mark as complete after all animations finish
      completionTimerRef.current = setTimeout(() => {
        setIsTyping(false);
        onComplete();
      }, totalTime);

    }, delay);

    return () => {
      clearTimeout(showTimer);
      if (completionTimerRef.current) {
        clearTimeout(completionTimerRef.current);
      }
    };
  }, [message, delay, animated, onComplete, messageChars.length]);

  // Handle message overflow
  const handleScroll = () => {
    if (messageRef.current) {
      messageRef.current.scrollTop = messageRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    handleScroll();
  }, [message]);

  if (!isVisible && animated) {
    return null;
  }

  return (
    <div className={`chat-bubble-container ${isVisible ? 'visible' : ''}`}>
      {/* Avatar */}
      <div className="chat-avatar">
        <div className="avatar-circle">
          <span className="avatar-emoji">{avatar}</span>
        </div>
        
        {/* Typing indicator */}
        {(typing || isTyping) && (
          <div className="typing-indicator">
            <div className="typing-dots">
              <span className="dot"></span>
              <span className="dot"></span>
              <span className="dot"></span>
            </div>
          </div>
        )}
      </div>

      {/* Chat bubble */}
      <div className={`chat-bubble ${isTyping ? 'typing' : ''}`}>
        {showTail && <div className="bubble-tail"></div>}
        
        <div
          ref={messageRef}
          className="bubble-content"
        >
          <div className="message-text">
            {animated && showLetters ? (
              messageChars.map((char, index) => {
                // Preserve spaces with proper rendering
                const displayChar = char === ' ' ? '\u00A0' : char;
                return (
                  <span
                    key={index}
                    className="letter-animated"
                    style={{ '--letter-delay': `${index * 30}ms` }}
                  >
                    {displayChar}
                  </span>
                );
              })
            ) : (
              message
            )}
          </div>

          {/* Message reactions/feedback (for future use) */}
          {!isTyping && message && (
            <div className="message-reactions">
              {/* Could add thumbs up/down or other reactions */}
            </div>
          )}
        </div>

        {/* Bubble animation effects */}
        <div className="bubble-effects">
          {/* Sparkle effect for special messages */}
          {message && message.includes('🎉') && (
            <div className="sparkle-effect">
              <span className="sparkle">✨</span>
            </div>
          )}

          {/* Pulse effect during typing */}
          {isTyping && (
            <div className="pulse-effect"></div>
          )}
        </div>
      </div>

      {/* Gideon's mood indicator */}
      <div className="gideon-mood">
        {isTyping ? (
          <div className="mood-thinking">🤔</div>
        ) : message && message.includes('🎉') ? (
          <div className="mood-excited">😄</div>
        ) : message && message.includes('?') ? (
          <div className="mood-curious">🤨</div>
        ) : (
          <div className="mood-friendly">😊</div>
        )}
      </div>
    </div>
  );
};

// Enhanced chat bubble with multiple message support
export const ChatConversation = ({ messages, onMessageComplete }) => {
  const [visibleMessages, setVisibleMessages] = useState([]);
  const conversationRef = useRef(null);

  // Handle message completion callbacks
  const handleComplete = (index) => {
    if (onMessageComplete) {
      onMessageComplete(index);
    }
  };

  useEffect(() => {
    if (messages.length === 0) return;

    // Show messages one by one
    let messageIndex = 0;
    const showNextMessage = () => {
      if (messageIndex < messages.length) {
        setVisibleMessages(prev => [...prev, messages[messageIndex]]);
        messageIndex++;
      }
    };

    // Start with first message
    showNextMessage();

    return () => {
      setVisibleMessages([]);
    };
  }, [messages, onMessageComplete]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  }, [visibleMessages]);

  return (
    <div className="chat-conversation" ref={conversationRef}>
      {visibleMessages.map((message, index) => (
        <OnboardingChatBubble
          key={index}
          message={message.text}
          avatar={message.avatar || '🎯'}
          delay={index === 0 ? 0 : 500}
          onComplete={() => handleComplete(index)}
          animated={message.animated !== false}
        />
      ))}
    </div>
  );
};

export default OnboardingChatBubble;